import React, { useRef } from "react";
import AuthenticityTokenField from "../../../components/forms/AuthenticityTokenField";
import { Card, CardContent } from "../../../components/ui/card";
import type { CredentialDetail, MainAppPageProps } from "../types";
import { CredentialShareToggle } from "./CredentialShareToggle";

type CredentialSharePanelProps = Pick<
  MainAppPageProps,
  "csrfToken" | "routes"
> & {
  credential: CredentialDetail;
};

export const CredentialSharePanel: React.FC<CredentialSharePanelProps> = ({
  csrfToken,
  routes,
  credential,
}) => {
  const action = (
    routes.dashboardCredential ?? "/dashboard/credentials/:id"
  ).replace(":id", credential.id);
  const shareAction = (
    routes.dashboardCredentialShare ?? "/dashboard/credentials/:id/shares"
  ).replace(":id", credential.id);
  const share = (credential.shares ?? [])[0];
  const active = Boolean(share?.active);
  const restoreRef = useRef<HTMLFormElement>(null);
  const revokeRef = useRef<HTMLFormElement>(null);
  const createRef = useRef<HTMLFormElement>(null);

  const handleToggle = () => {
    if (active && share) {
      revokeRef.current?.requestSubmit();
      return;
    }
    if (share) {
      restoreRef.current?.requestSubmit();
      return;
    }
    createRef.current?.requestSubmit();
  };

  return (
    <Card className="bg-card">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-base font-semibold text-foreground">Share</h2>
          {credential.canEdit !== false ? (
            <CredentialShareToggle checked={active} onClick={handleToggle} />
          ) : null}
        </div>
        {share ? (
          <a
            className="mt-4 block break-all text-sm text-premium-600 underline"
            href={share.url}
          >
            {share.url}
          </a>
        ) : null}
        <form
          ref={createRef}
          action={shareAction}
          method="post"
          className="hidden"
        >
          <AuthenticityTokenField csrfToken={csrfToken} />
        </form>
        {share ? (
          <>
            <form
              ref={restoreRef}
              action={`${action}/shares/${share.id}/restore`}
              method="post"
              className="hidden"
            >
              <AuthenticityTokenField csrfToken={csrfToken} />
            </form>
            <form
              ref={revokeRef}
              action={`${action}/shares/${share.id}`}
              method="post"
              className="hidden"
            >
              <AuthenticityTokenField csrfToken={csrfToken} />
              <input type="hidden" name="_method" value="delete" />
            </form>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};
